import React, { useState } from 'react';
import styled from 'styled-components';

import { MessageType } from '@common/hooks/useChatbot';
import { CodeOutlined, PublicOutlined } from '@mui/icons-material';
import { Chip, Collapse, IconButton, Stack } from '@mui/material';
import { Button, LoadingButton } from '@common/Button';
import { tabActions } from '@store/index';
import { useDispatch } from 'react-redux';
import { useMutation } from '@tanstack/react-query';
import { executeHttpTool } from '@requests/tools/http-tool.requests';

const Container = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  margin-top: 1rem;

  pre {
    white-space: pre-wrap;
    word-break: break-all;
    font-size: 12px;
    margin: 0;
  }
`;

const ApprovalBox = styled.div`
  display: flex;
  flex-direction: column;
  width: calc(100% - 1.5rem);
  border: 1px solid ${({ theme }) => theme.line.light};
  border-radius: 6px;
  padding: 0.75rem;

  h3 {
    font-size: 13px;
    font-weight: 400;
    margin: 0;
    line-height: 1.4;
  }

  p {
    color: ${({ theme }) => theme.text.subtext};
    font-size: 11px;
    margin: 0;
    line-height: 1.2;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 80%;
  }
`;

const ApprovalIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 36px;
  min-width: 36px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.line.light};
  color: ${({ theme }) => theme.primary};
  background-color: ${({ theme }) => theme.background.light};

  svg {
    font-size: 20px;
  }
`;

const RequestCode = styled.div`
  border-top: 1px solid ${({ theme }) => theme.line.light};
  margin-top: 0.75rem;
  padding: 0 0.25rem;
  padding-top: 0.75rem;

  & .MuiChip-label {
    text-transform: uppercase;
    color: ${({ theme }) => theme.primary};
    font-weight: bold;
  }
`;

interface Props {
  entry: MessageType;
}

export const MessageToolApproval: React.FC<Props> = ({ entry }) => {
  const dispatch = useDispatch();
  const approved = entry.events.toolApproval?.approved;

  const [showCode, setShowCode] = useState(false);

  const { mutateAsync: execute, isPending: loading } = useMutation({
    mutationFn: executeHttpTool,
    onSuccess: () => {
      dispatch(
        tabActions.chatAddMessageApproveToolApproval({
          id: entry.id,
          node_id: entry.node_id,
          workflow_id: entry.workflow_id,
        })
      );
    },
  });

  const toolApproval = entry.events.toolApproval;
  if (!toolApproval) return null;

  const executeToolApproval = async () => {
    try {
      await execute(toolApproval);
    } catch (error) {
      console.error(error);
    }
  };

  const cancelToolApproval = () => {
    dispatch(
      tabActions.chatAddMessageRemoveToolApproval({
        id: entry.id,
        node_id: entry.node_id,
        workflow_id: entry.workflow_id,
      })
    );
  };

  return (
    <Container>
      <ApprovalBox>
        <Stack direction='row' alignItems='center' justifyContent='space-between'>
          <Stack spacing={1.25} direction='row' alignItems='center' width='100%'>
            <ApprovalIcon>
              <PublicOutlined />
            </ApprovalIcon>
            <Stack justifyContent='center' flex={1}>
              <h3>HTTP Request</h3>
              <p>{toolApproval.action}</p>
            </Stack>
          </Stack>
          {approved && <Chip label='Executed' size='small' color='success' />}
          {!approved && (
            <Stack spacing={1} direction='row' alignItems='center' sx={{ minWidth: loading ? '120px' : '200px' }}>
              {!loading && (
                <Button small onClick={cancelToolApproval}>
                  Cancel
                </Button>
              )}
              <LoadingButton
                loading={loading}
                minWidth='80px'
                minHeight='27px'
                small
                customColor='black'
                onClick={executeToolApproval}
              >
                Confirm
              </LoadingButton>
              <IconButton size='small' onClick={() => setShowCode(!showCode)}>
                <CodeOutlined fontSize='small' />
              </IconButton>
            </Stack>
          )}
        </Stack>
        <Collapse in={showCode}>
          <RequestCode>
            <Stack spacing={1} direction='row' alignItems='center'>
              <Chip variant='outlined' size='small' label={toolApproval.data.method} />
              <pre>{toolApproval.data.url}</pre>
            </Stack>
          </RequestCode>
        </Collapse>
      </ApprovalBox>
    </Container>
  );
};
